/**
 * Universal deep-link entry point for Lumera.
 *
 * External apps (Slidecon, email links, shared chat, etc.) can construct
 * one canonical URL and have it open the right place inside Lumera:
 *
 *   ${LUMERA_PUBLIC_URL}/open?ref=John+3:16&lens=1
 *   ${LUMERA_PUBLIC_URL}/open?ref=Genesis+1:1
 *   ${LUMERA_PUBLIC_URL}/open?topic=grace+and+forgiveness&study=1
 *   ${LUMERA_PUBLIC_URL}/open?draft_id=<sermon_draft_id>
 *
 * Recognised query params:
 *   ref         — a Bible reference like "John 3:16" or "1 Samuel 3:16"
 *                 → navigates to /bible?book=…&chapter=…&verse=… and
 *                   optionally fires the lumera:verse-lens event after mount.
 *   lens=1      — when present alongside ref, opens the unified 7-source
 *                 Verse Lens side panel right away.
 *   topic       — sermon topic; combined with study=1 routes to
 *                 /sermon-prep with the topic pre-filled.
 *   study=1     — explicit study/sermon-prep intent.
 *   draft_id    — open a specific sermon draft directly.
 *
 * On any malformed input the page redirects to /dashboard with a soft toast.
 */
import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const REF_RE = /^\s*((?:[1-3]\s)?[A-Za-z][A-Za-z\s]+?)\s+(\d+):(\d+)(?:-\d+)?\s*$/;

const parseRef = (raw) => {
  if (!raw) return null;
  const m = String(raw).match(REF_RE);
  if (!m) return null;
  return { book: m[1].trim(), chapter: parseInt(m[2], 10), verse: parseInt(m[3], 10) };
};

const OpenDeepLink = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, loading } = useAuth();

  useEffect(() => {
    if (loading) return; // wait for auth state before deciding where to land
    const ref = params.get('ref');
    const lens = params.get('lens') === '1';
    const topic = params.get('topic');
    const study = params.get('study') === '1';
    const draftId = params.get('draft_id');

    // 1. Direct draft open
    if (draftId) {
      navigate(`/sermon-prep?draft_id=${encodeURIComponent(draftId)}`, { replace: true });
      return;
    }

    // 2. Topic-led sermon prep
    if (study && topic) {
      navigate(`/sermon-prep?topic=${encodeURIComponent(topic)}`, { replace: true });
      return;
    }

    // 3. Scripture-led
    if (ref) {
      const parsed = parseRef(ref);
      if (!parsed) {
        toast.error(`Could not parse reference: ${ref}`);
        navigate(user ? '/dashboard' : '/', { replace: true });
        return;
      }
      // Visitors who aren't logged in get a free, read-only preview of the
      // actual passage instead of a login wall — better for conversion.
      if (!user) {
        navigate(`/read?ref=${encodeURIComponent(ref)}`, { replace: true });
        return;
      }
      // Logged-in users go to the full Bible reader (+ optional Verse Lens).
      // The reader reads /bible/:book/:chapter path params, not query params.
      navigate(`/bible/${encodeURIComponent(parsed.book)}/${parsed.chapter}?verse=${parsed.verse}`, { replace: true });
      if (lens) {
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('lumera:verse-lens', { detail: parsed }));
        }, 600);
      }
      return;
    }

    // 4. Nothing recognised — soft fallback
    toast.message('Lumera is ready — pick where to start.');
    navigate(user ? '/dashboard' : '/', { replace: true });
  }, [navigate, params, user, loading]);

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950"
      data-testid="lumera-open-deeplink"
    >
      <div className="text-center">
        <div className="inline-block w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mb-3" />
        <p className="text-sm text-slate-600 dark:text-slate-400">Opening Lumera…</p>
      </div>
    </div>
  );
};

export default OpenDeepLink;
